import {useState} from "react";
import {UserLogin} from "../../../api";
import {showNotification} from "../../../actions";
import {useDispatch} from "react-redux";
import {defineMessage} from "react-intl";
import SharedUserRow from "./SharedUserRow";
import {
    useDeleteExtensionUserMutation,
    useMakeOwnerMutation,
    useResendInvitationEmailMutation,
} from "./queries.generated";
import {ContextFilter} from "@/context/AuthProvider";
import {Permission} from "@/authSchema";

type Props = {
    user: UserLogin;
    actionsPermission: {name: Permission; contextFilter: ContextFilter};
    onDelete: () => void;
    onMakeOwner: () => void;
    extensionId: number;
    changeOwnerDisabled: boolean;
};

export default function UserRow({
    user,
    actionsPermission,
    onDelete,
    onMakeOwner,
    extensionId,
    changeOwnerDisabled,
}: Props) {
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [isMakeOwnerDialogOpen, setMakeOwnerDialogOpen] = useState(false);
    const [isResendInvitationEmailDialogOpen, setIsResendInvitationEmailDialogOpen] = useState(false);
    const [mutationDeleteExtensionUser] = useDeleteExtensionUserMutation();
    const [mutationMakeOwner] = useMakeOwnerMutation();
    const [mutationResendInvitationEmail] = useResendInvitationEmailMutation();
    const dispatch = useDispatch();

    function deleteUser() {
        mutationDeleteExtensionUser({variables: {userSub: user.userSub, userId: extensionId}})
            .then((result) => {
                if (!result.data.deleteUserLogin.length) {
                    onDelete();
                    return;
                }
                setDialogOpen(false);
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            })
            .catch(() => {
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            });
    }

    function makeOwner() {
        mutationMakeOwner({variables: {userSub: user.userSub, userId: extensionId}})
            .then((result) => {
                if (!result.data.makeOwner.length) {
                    onMakeOwner();
                    return;
                }
                setMakeOwnerDialogOpen(false);
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            })
            .catch(() => {
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            });
    }

    const resendEmail = () => {
        mutationResendInvitationEmail({variables: {userId: extensionId, email: user.emails[0]}})
            .then((result) => {
                if (!result.data.resendInvitationEmail.errors) {
                    dispatch(
                        showNotification(
                            defineMessage({
                                id: "userManagement.notifications.emailSent",
                                defaultMessage: "Email sent",
                            }),
                            "success",
                        ),
                    );
                    return;
                }
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            })
            .catch(() => {
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            });
    };

    return (
        <SharedUserRow
            user={user}
            actionsPermission={actionsPermission}
            isDeleteUserDialogOpen={isDialogOpen}
            isMakeOwnerDialogOpen={isMakeOwnerDialogOpen}
            isResendInvitationEmailDialogOpen={isResendInvitationEmailDialogOpen}
            deleteUser={() => deleteUser()}
            makeOwner={() => makeOwner()}
            resendEmail={() => resendEmail()}
            openDeleteUserDialog={() => setDialogOpen(true)}
            openMakeOwnerDialog={() => setMakeOwnerDialogOpen(true)}
            openResendInvitationEmailDialog={() => setIsResendInvitationEmailDialogOpen(true)}
            closeDeleteUserDialog={() => setDialogOpen(false)}
            closeMakeOwnerDialog={() => setMakeOwnerDialogOpen(false)}
            closeResendInvitationEmailDialog={() => setIsResendInvitationEmailDialogOpen(false)}
            changeOwnerDisabled={changeOwnerDisabled}
        />
    );
}
